import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  Stack,
  TextField,
  IconButton,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import trash2Fill from "@iconify-icons/eva/trash-2-fill";
import plusFill from "@iconify-icons/eva/plus-fill";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import { AddZona, GetZonas, DeleteZona } from "src/redux/slices/mesas";

// ----------------------------------------------------------------------
//
//   Step 1 · Zonas. Chips templates según localKind + añadir custom.
//   Objetivo: que el user llegue a 2-4 zonas con 2 clicks.
//
// ----------------------------------------------------------------------

const TEMPLATES = {
  restaurant: [
    { icon: "🛋️", name: "Salón" },
    { icon: "🌳", name: "Terraza" },
    { icon: "🍽️", name: "Reservado" },
    { icon: "🍸", name: "Barra" },
  ],
  bar: [
    { icon: "🍺", name: "Barra" },
    { icon: "🌳", name: "Terraza" },
    { icon: "🪑", name: "Mesas altas" },
    { icon: "🎯", name: "Zona ocio" },
  ],
  cafe: [
    { icon: "🍰", name: "Barra" },
    { icon: "🌳", name: "Terraza" },
    { icon: "☕", name: "Mesas" },
    { icon: "📦", name: "Take-away" },
  ],
  chiringuito: [
    { icon: "🏖️", name: "Terraza playa" },
    { icon: "🍹", name: "Barra" },
    { icon: "🌴", name: "Chill-out" },
  ],
  other: [
    { icon: "🛋️", name: "Salón" },
    { icon: "🌳", name: "Terraza" },
    { icon: "🍸", name: "Barra" },
  ],
};

export default function StepZonas({ progress, onComplete }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const zonas = useSelector((s) => s.mesas.zonas || []);
  const [customName, setCustomName] = useState("");
  const [saving, setSaving] = useState(false);

  const templates = TEMPLATES[progress?.localKind || "other"] || TEMPLATES.other;
  const zonaNames = new Set((zonas || []).map((z) => (z.nombre || "").toLowerCase()));

  useEffect(() => {
    dispatch(GetZonas());
  }, [dispatch]);

  const handleAddTemplate = async (name) => {
    if (zonaNames.has(name.toLowerCase())) return;
    try {
      await dispatch(AddZona({ nombre: name }));
    } catch (err) {
      enqueueSnackbar(err?.message || "Error creando zona", { variant: "error" });
    }
  };

  const handleAddCustom = async () => {
    const name = customName.trim();
    if (!name || zonaNames.has(name.toLowerCase())) return;
    try {
      await dispatch(AddZona({ nombre: name }));
      setCustomName("");
    } catch (err) {
      enqueueSnackbar(err?.message || "Error creando zona", { variant: "error" });
    }
  };

  const handleDelete = async (zona) => {
    try {
      await dispatch(DeleteZona(zona.id));
    } catch (err) {
      enqueueSnackbar(err?.message || "Error eliminando zona", { variant: "error" });
    }
  };

  const handleContinue = async () => {
    if (zonas.length === 0) {
      enqueueSnackbar("Añade al menos una zona", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      await onComplete({ dataJson: JSON.stringify({ zonasCount: zonas.length }) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        ¿Qué zonas tiene tu local?
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 4 }}>
        Añade las zonas donde vas a tener mesas. Puedes empezar por las plantillas
        y personalizarlas después.
      </Typography>

      {/* Plantillas rápidas */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Sugerencias para tu tipo de local
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 3 }}>
        {templates.map((t) => {
          const isAdded = zonaNames.has(t.name.toLowerCase());
          return (
            <Chip
              key={t.name}
              label={`${t.icon}  ${t.name}`}
              onClick={() => !isAdded && handleAddTemplate(t.name)}
              variant={isAdded ? "filled" : "outlined"}
              color={isAdded ? "success" : "default"}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                py: 2.5,
                cursor: isAdded ? "default" : "pointer",
              }}
            />
          );
        })}
      </Stack>

      {/* Añadir custom */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, maxWidth: 480 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Nombre zona personalizada"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
        />
        <Button
          variant="outlined"
          startIcon={<Icon icon={plusFill} />}
          onClick={handleAddCustom}
          disabled={!customName.trim()}
        >
          Añadir
        </Button>
      </Stack>

      {/* Zonas ya creadas */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Zonas de tu local ({zonas.length})
      </Typography>
      <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 3 }}>
        {zonas.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "text.disabled", py: 3, textAlign: "center" }}>
              Aún no has añadido ninguna zona. Empieza tocando una plantilla arriba.
            </Typography>
          </Grid>
        )}
        {zonas.map((z) => (
          <Grid item xs={12} sm={6} md={4} key={z.id}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 700 }}>{z.nombre}</Typography>
              <IconButton size="small" color="error" onClick={() => handleDelete(z)}>
                <Icon icon={trash2Fill} />
              </IconButton>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ textAlign: "right", mt: 4 }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleContinue}
          disabled={saving || zonas.length === 0}
        >
          {saving ? "Guardando…" : "Continuar →"}
        </Button>
      </Box>
    </Box>
  );
}
